import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, Tab, Transition } from '@headlessui/react';
import { X, Upload, Users, FileSpreadsheet, AlertTriangle, CheckCircle, XCircle, Loader2, Download, Plus, Trash2 } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import participantService from '../../services/participantService';

const BATCH_LIMIT = 500;
const DEFAULT_PIN = '987789';

// Steps: input -> preview -> submitting -> results
const STEPS = { INPUT: 'input', PREVIEW: 'preview', SUBMITTING: 'submitting', RESULTS: 'results' };

const BulkCreateParticipantsModal = ({ isOpen, onClose, onSuccess, studyId, siteId = null, sites = [], studyName = '', sponsorCode = '' }) => {
  const [step, setStep] = useState(STEPS.INPUT);
  const [activeTab, setActiveTab] = useState(0);
  const [csvRows, setCsvRows] = useState([]);
  const [generateEntries, setGenerateEntries] = useState([]);
  const [globalPin, setGlobalPin] = useState(DEFAULT_PIN);
  const [globalEnrollmentDate, setGlobalEnrollmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [results, setResults] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  // FECOA-1609: live window-generation progress for the just-created batch.
  const [genProgress, setGenProgress] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(STEPS.INPUT);
      setCsvRows([]);
      setGenerateEntries(siteId ? [{ site_code: sites[0]?.site_code || '', min_number: '', max_number: '' }] : []);
      setGlobalPin(DEFAULT_PIN);
      setGlobalEnrollmentDate(new Date().toISOString().split('T')[0]);
      setResults(null);
      setGenProgress(null);
      setCsvFileName('');
      setActiveTab(0);
    }
  }, [isOpen, siteId, sites]);

  // FECOA-1609: once participants are created, their survey windows generate
  // asynchronously. Poll the study-scoped status endpoint and join against the
  // created batch's UUIDs so the "N of M ready" reflects only this import.
  useEffect(() => {
    if (step !== STEPS.RESULTS || !studyId) return;
    const createdIds = new Set((results?.created || []).map((p) => p.id).filter(Boolean));
    if (createdIds.size === 0) return;

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const data = await participantService.getGenerationStatus({ studyID: studyId });
        if (cancelled) return;
        const batch = (data.participants || []).filter((p) => createdIds.has(p.participant_id));
        const ready = batch.filter((p) => p.status === 'complete').length;
        const failed = batch.filter((p) => p.status === 'failed').length;
        setGenProgress({ total: createdIds.size, ready, failed });
        // Keep polling until every created participant is terminal (complete/failed).
        if (ready + failed < createdIds.size) {
          timer = setTimeout(poll, 4000);
        }
      } catch {
        // Transient errors shouldn't break the results view; retry on the interval.
        if (!cancelled) timer = setTimeout(poll, 8000);
      }
    };

    setGenProgress({ total: createdIds.size, ready: 0, failed: 0 });
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [step, studyId, results]);

  // CSV parsing
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setCsvFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        toast.error('CSV must have a header row and at least one data row');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const siteIdx = headers.indexOf('site');
      const numIdx = headers.indexOf('participant_number');
      const activeIdx = headers.indexOf('is_active');
      const dateIdx = headers.indexOf('enrollment_date');
      const pinIdx = headers.indexOf('pin');
      const armIdx = headers.indexOf('arm_id');

      if (siteIdx === -1 || numIdx === -1) {
        toast.error('CSV must have "site" and "participant_number" columns');
        return;
      }

      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim());
        if (!cols[siteIdx] || !cols[numIdx]) continue;

        const row = {
          site_code: cols[siteIdx],
          participant_number: parseInt(cols[numIdx], 10),
          is_active: activeIdx !== -1 ? cols[activeIdx]?.toLowerCase() !== 'false' : true,
          enrollment_date: dateIdx !== -1 && cols[dateIdx] ? cols[dateIdx] : null,
          pin: pinIdx !== -1 && cols[pinIdx] ? cols[pinIdx] : null,
          arm_id: armIdx !== -1 && cols[armIdx] ? cols[armIdx] : null,
        };

        if (isNaN(row.participant_number)) continue;

        // Filter by siteId if scoped to single site
        if (siteId && row.site_code !== (sites[0]?.site_code || '')) {
          continue;
        }

        rows.push(row);
      }

      if (rows.length === 0) {
        toast.error('No valid rows found in CSV');
        return;
      }

      if (rows.length > BATCH_LIMIT) {
        toast.error(`Maximum ${BATCH_LIMIT} participants per batch. Found ${rows.length}.`);
        return;
      }

      setCsvRows(rows);
    };
    reader.readAsText(file);
  }, [siteId, sites]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
  });

  // Generate mode helpers
  const addSiteEntry = () => {
    setGenerateEntries(prev => [...prev, { site_code: '', min_number: '', max_number: '' }]);
  };

  const removeSiteEntry = (idx) => {
    setGenerateEntries(prev => prev.filter((_, i) => i !== idx));
  };

  const updateSiteEntry = (idx, field, value) => {
    setGenerateEntries(prev => prev.map((entry, i) => i === idx ? { ...entry, [field]: value } : entry));
  };

  // Calculate totals for generate mode
  const generateTotal = generateEntries.reduce((sum, entry) => {
    const min = parseInt(entry.min_number, 10);
    const max = parseInt(entry.max_number, 10);
    if (!isNaN(min) && !isNaN(max) && max >= min) {
      return sum + (max - min + 1);
    }
    return sum;
  }, 0);

  // Get rows for preview (from either tab)
  const getPreviewRows = () => {
    if (activeTab === 0) return csvRows;
    // Generate mode: expand ranges
    const rows = [];
    for (const entry of generateEntries) {
      const min = parseInt(entry.min_number, 10);
      const max = parseInt(entry.max_number, 10);
      if (isNaN(min) || isNaN(max) || max < min || !entry.site_code) continue;
      for (let n = min; n <= max; n++) {
        rows.push({
          site_code: entry.site_code,
          participant_number: n,
          is_active: true,
          enrollment_date: globalEnrollmentDate || null,
          pin: globalPin || null,
          arm_id: null,
        });
      }
    }
    return rows;
  };

  const canProceedToPreview = () => {
    if (activeTab === 0) return csvRows.length > 0;
    return generateTotal > 0 && generateTotal <= BATCH_LIMIT && generateEntries.every(e => e.site_code && e.min_number && e.max_number);
  };

  // Check for past enrollment dates
  const hasPastDates = () => {
    const today = new Date().toISOString().split('T')[0];
    const rows = getPreviewRows();
    return rows.some(r => r.enrollment_date && r.enrollment_date < today);
  };

  // Submit
  const handleSubmit = async () => {
    setStep(STEPS.SUBMITTING);
    try {
      let payload;
      if (activeTab === 0) {
        payload = { mode: 'csv', participants: csvRows };
      } else {
        payload = {
          mode: 'generate',
          sites: generateEntries.filter(e => e.site_code && e.min_number && e.max_number).map(e => ({
            site_code: e.site_code,
            min_number: parseInt(e.min_number, 10),
            max_number: parseInt(e.max_number, 10),
          })),
          enrollment_date: globalEnrollmentDate || undefined,
          pin: globalPin || undefined,
        };
      }

      const data = await participantService.bulkCreateParticipants(studyId, payload);
      setResults(data);
      setStep(STEPS.RESULTS);

      if (data.total_created > 0) {
        toast.success(`Created ${data.total_created} participant${data.total_created !== 1 ? 's' : ''}`);
        onSuccess?.();
      }
      if (data.total_failed > 0) {
        toast.error(`${data.total_failed} participant${data.total_failed !== 1 ? 's' : ''} failed`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Bulk creation failed');
      setStep(STEPS.PREVIEW);
    }
  };

  const downloadTemplate = () => {
    const csv = 'site,participant_number,is_active,enrollment_date,pin,arm_id\n5001,1001,true,2026-01-01,987789,\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_participants_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    if (step === STEPS.SUBMITTING) return; // Don't close during submission
    onClose();
  };

  const availableSiteCodes = sites.map(s => s.site_code);

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog onClose={handleClose}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-950/40 dark:bg-black/70" onClick={handleClose} />
        </Transition.Child>

        {/* Panel */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2" enterTo="opacity-100 translate-y-0"
          leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-2"
        >
          <Dialog.Panel
            className="relative z-10 w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10 flex flex-col transform transition-all"
          >
            {/* Header */}
            <div className="card-band flex-nowrap">
              <Dialog.Title className="card-band-title flex items-center gap-2">
                <Users className="h-5 w-5" />
                Bulk Add Participants
                {siteId && sites[0] && <span className="ml-1 text-sm font-normal text-primary-100">({sites[0].site_code})</span>}
              </Dialog.Title>
              <button onClick={handleClose} aria-label="Close"
                className="-mr-2 flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
                disabled={step === STEPS.SUBMITTING}>
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {step === STEPS.INPUT && (
                <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                  <Tab.List className="segmented mb-6 flex w-full">
                    <Tab className={({ selected }) =>
                      `segmented-item flex-1 justify-center py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${selected ? 'segmented-item-active' : ''}`
                    }>
                      <Upload className="h-4 w-4" />CSV Upload
                    </Tab>
                    <Tab className={({ selected }) =>
                      `segmented-item flex-1 justify-center py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${selected ? 'segmented-item-active' : ''}`
                    }>
                      <Users className="h-4 w-4" />Generate
                    </Tab>
                  </Tab.List>

                  <Tab.Panels>
                    {/* CSV Upload Tab */}
                    <Tab.Panel>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-600 dark:text-gray-300">Upload a CSV file with participant data.</p>
                          <button onClick={downloadTemplate}
                            className="link inline-flex items-center gap-1.5 text-sm">
                            <Download className="h-4 w-4" /> Download Template
                          </button>
                        </div>

                        <div {...getRootProps()}
                          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                            ${isDragActive ? 'border-primary-400 bg-primary-50 dark:bg-primary-500/10' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50/60 dark:border-gray-600 dark:hover:bg-white/[0.02]'}`}
                        >
                          <input {...getInputProps()} />
                          <FileSpreadsheet className="w-10 h-10 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
                          {csvFileName ? (
                            <p className="text-sm font-medium text-primary-700 dark:text-primary-400">{csvFileName} ({csvRows.length} rows)</p>
                          ) : isDragActive ? (
                            <p className="text-sm text-primary-700 dark:text-primary-400">Drop the CSV file here...</p>
                          ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">Drag & drop a CSV file, or click to select</p>
                          )}
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Required: site, participant_number. Optional: is_active, enrollment_date, pin, arm_id</p>
                        </div>

                        {csvRows.length > 0 && (
                          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Preview ({csvRows.length} participants)</p>
                            <div className="max-h-48 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead className="font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                  <tr>
                                    <th className="text-left py-1 px-2">Site</th>
                                    <th className="text-left py-1 px-2">#</th>
                                    <th className="text-left py-1 px-2">Active</th>
                                    <th className="text-left py-1 px-2">Enrollment Date</th>
                                    <th className="text-left py-1 px-2">PIN</th>
                                    <th className="text-left py-1 px-2">Arm</th>
                                  </tr>
                                </thead>
                                <tbody className="text-gray-700 dark:text-gray-300">
                                  {csvRows.slice(0, 20).map((row, i) => (
                                    <tr key={i} className="border-t border-gray-200 dark:border-gray-700">
                                      <td className="py-1 px-2">{row.site_code}</td>
                                      <td className="py-1 px-2">{row.participant_number}</td>
                                      <td className="py-1 px-2">{row.is_active ? 'Yes' : 'No'}</td>
                                      <td className="py-1 px-2">{row.enrollment_date || 'Today'}</td>
                                      <td className="py-1 px-2">{row.pin || DEFAULT_PIN}</td>
                                      <td className="py-1 px-2">{row.arm_id || 'Auto'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {csvRows.length > 20 && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">...and {csvRows.length - 20} more</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </Tab.Panel>

                    {/* Generate Tab */}
                    <Tab.Panel>
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Select sites and define participant number ranges to auto-generate.
                        </p>

                        {/* Site entries */}
                        {generateEntries.map((entry, idx) => (
                          <div key={idx} className="flex items-end gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                            <div className="flex-1">
                              <label className="label">Site</label>
                              {siteId ? (
                                <input type="text" value={entry.site_code} readOnly
                                  className="input bg-gray-100 text-gray-600 dark:bg-white/[0.03] dark:text-gray-300" />
                              ) : (
                                <select value={entry.site_code}
                                  onChange={(e) => updateSiteEntry(idx, 'site_code', e.target.value)}
                                  className="input">
                                  <option value="">Select site...</option>
                                  {availableSiteCodes.map(code => (
                                    <option key={code} value={code}>{code}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <div className="w-28">
                              <label className="label">Min #</label>
                              <input type="number" value={entry.min_number}
                                onChange={(e) => updateSiteEntry(idx, 'min_number', e.target.value)}
                                placeholder="1001"
                                className="input" />
                            </div>
                            <div className="w-28">
                              <label className="label">Max #</label>
                              <input type="number" value={entry.max_number}
                                onChange={(e) => updateSiteEntry(idx, 'max_number', e.target.value)}
                                placeholder="1050"
                                className="input" />
                            </div>
                            {!siteId && generateEntries.length > 1 && (
                              <button onClick={() => removeSiteEntry(idx)} aria-label="Remove site"
                                className="btn btn-danger-soft btn-icon h-10 w-10">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}

                        {!siteId && (
                          <button onClick={addSiteEntry}
                            className="link inline-flex items-center gap-1.5 text-sm">
                            <Plus className="h-4 w-4" /> Add another site
                          </button>
                        )}

                        {/* Global settings */}
                        <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700">
                          <div>
                            <label className="label">Enrollment Date</label>
                            <input type="date" value={globalEnrollmentDate}
                              onChange={(e) => setGlobalEnrollmentDate(e.target.value)}
                              className="input" />
                          </div>
                          <div>
                            <label className="label">PIN (all participants)</label>
                            <input type="text" value={globalPin}
                              onChange={(e) => setGlobalPin(e.target.value)}
                              placeholder={DEFAULT_PIN}
                              className="input" />
                          </div>
                        </div>

                        {/* Summary */}
                        {generateTotal > 0 && (
                          <div className={`text-sm font-medium px-3 py-2 rounded-lg ${
                            generateTotal > BATCH_LIMIT ? 'bg-danger-50 text-danger-700 dark:bg-danger-500/10 dark:text-danger-400' : 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300'
                          }`}>
                            Will create {generateTotal} participant{generateTotal !== 1 ? 's' : ''}
                            {generateTotal > BATCH_LIMIT && ` (exceeds limit of ${BATCH_LIMIT})`}
                          </div>
                        )}
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
              )}

              {step === STEPS.PREVIEW && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Confirm Bulk Creation</h3>

                  {hasPastDates() && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
                      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500 dark:text-amber-400" />
                      <div className="text-sm text-amber-800 dark:text-amber-200">
                        <p className="font-medium">Retroactive enrollment dates detected</p>
                        <p>Survey windows for past dates will be generated and automatically marked as missed.</p>
                      </div>
                    </div>
                  )}

                  {/* Summary by site */}
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                    <table className="w-full text-sm">
                      <thead className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="text-left py-1">Site</th>
                          <th className="text-right py-1">Count</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-700 dark:text-gray-300">
                        {Object.entries(getPreviewRows().reduce((acc, r) => {
                          acc[r.site_code] = (acc[r.site_code] || 0) + 1;
                          return acc;
                        }, {})).map(([code, count]) => (
                          <tr key={code} className="border-t border-gray-200 dark:border-gray-700">
                            <td className="py-1">{code}</td>
                            <td className="text-right py-1">{count}</td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-gray-300 font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                          <td className="py-1">Total</td>
                          <td className="text-right py-1">{getPreviewRows().length}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {step === STEPS.SUBMITTING && (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="mb-4 h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
                  <p className="text-gray-600 dark:text-gray-300 font-medium">Creating participants...</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">This may take a moment for large batches</p>
                </div>
              )}

              {step === STEPS.RESULTS && results && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Results</h3>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-center dark:border-gray-700 dark:bg-gray-900/40">
                      <p className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-gray-100">{results.total_requested}</p>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Requested</p>
                    </div>
                    <div className="rounded-xl border border-success-500/30 bg-success-50 p-3 text-center dark:bg-success-500/10">
                      <p className="text-2xl font-extrabold tabular-nums text-success-600 dark:text-success-400">{results.total_created}</p>
                      <p className="text-xs font-medium text-success-700 dark:text-success-400">Created</p>
                    </div>
                    <div className={`rounded-xl border p-3 text-center ${results.total_failed > 0 ? 'border-danger-500/30 bg-danger-50 dark:bg-danger-500/10' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40'}`}>
                      <p className={`text-2xl font-extrabold tabular-nums ${results.total_failed > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-gray-400 dark:text-gray-500'}`}>
                        {results.total_failed}
                      </p>
                      <p className={`text-xs font-medium ${results.total_failed > 0 ? 'text-danger-700 dark:text-danger-400' : 'text-gray-500 dark:text-gray-400'}`}>Failed</p>
                    </div>
                  </div>

                  {genProgress && genProgress.total > 0 && (() => {
                    const done = genProgress.ready + genProgress.failed >= genProgress.total;
                    const building = !done;
                    return (
                      <div className={`rounded-lg p-4 flex items-center gap-3 ${building ? 'bg-warning-500/10 border border-warning-500/30' : 'bg-success-500/10 border border-success-500/30'}`}>
                        {building
                          ? <Loader2 className="w-4 h-4 text-warning-500 animate-spin shrink-0" />
                          : <CheckCircle className="w-4 h-4 text-success-500 shrink-0" />}
                        <div className="text-sm">
                          <p className={`font-medium ${building ? 'text-warning-600 dark:text-warning-400' : 'text-success-600 dark:text-success-400'}`}>
                            {building
                              ? `Setting up surveys: ${genProgress.ready} of ${genProgress.total} ready`
                              : `Survey setup complete: ${genProgress.ready} of ${genProgress.total} ready`}
                          </p>
                          {genProgress.failed > 0 && (
                            <p className="text-xs text-danger-600 dark:text-danger-400 mt-0.5">
                              {genProgress.failed} participant{genProgress.failed !== 1 ? 's' : ''} failed to generate. Check the participant detail page.
                            </p>
                          )}
                          {building && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Windows generate in the background; you can close this and check participant detail pages.
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {results.errors?.length > 0 && (
                    <div className="rounded-lg border border-danger-500/30 bg-danger-50 p-4 dark:bg-danger-500/10">
                      <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-danger-800 dark:text-danger-400">
                        <XCircle className="h-4 w-4" /> Errors
                      </p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {results.errors.map((err, i) => (
                          <div key={i} className="rounded-md bg-danger-100 px-2 py-1 text-xs text-danger-700 dark:bg-danger-500/15 dark:text-danger-400">
                            <span className="font-medium">Row {err.row} ({err.site_code}-{err.participant_number}):</span> {err.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.created?.length > 0 && (
                    <div className="rounded-lg border border-success-500/30 bg-success-50 p-4 dark:bg-success-500/10">
                      <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-success-800 dark:text-success-400">
                        <CheckCircle className="h-4 w-4" /> Created Successfully
                      </p>
                      <div className="max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="font-medium uppercase tracking-wide text-success-700 dark:text-success-400">
                            <tr>
                              <th className="text-left py-1 px-2">Participant ID</th>
                              <th className="text-left py-1 px-2">Site</th>
                              <th className="text-left py-1 px-2">Arm</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.created.map((p, i) => (
                              <tr key={i} className="border-t border-success-500/20">
                                <td className="py-1 px-2 text-success-800 dark:text-success-400">{p.participant_id}</td>
                                <td className="py-1 px-2 text-success-700 dark:text-success-400">{p.site_code}</td>
                                <td className="py-1 px-2 text-success-700 dark:text-success-400">{p.arm_name || p.arm_id}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
              {step === STEPS.INPUT && (
                <>
                  <button onClick={handleClose}
                    className="btn btn-secondary">
                    Cancel
                  </button>
                  <button onClick={() => setStep(STEPS.PREVIEW)}
                    disabled={!canProceedToPreview()}
                    className="btn btn-primary">
                    Review
                  </button>
                </>
              )}
              {step === STEPS.PREVIEW && (
                <>
                  <button onClick={() => setStep(STEPS.INPUT)}
                    className="btn btn-secondary">
                    Back
                  </button>
                  <button onClick={handleSubmit}
                    className="btn btn-primary">
                    Create {getPreviewRows().length} Participant{getPreviewRows().length !== 1 ? 's' : ''}
                  </button>
                </>
              )}
              {step === STEPS.RESULTS && (
                <button onClick={handleClose}
                  className="btn btn-primary">
                  Close
                </button>
              )}
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition.Root>
  );
};

export default BulkCreateParticipantsModal;
